<template>
  <div>
    <v-dialog v-model="showConfigChat" max-width="800px" scrollable @update:model-value="onDialogToggle">
      <v-card class="config-chat-card d-flex flex-column" height="80vh">
        <v-card-title class="flex-shrink-0 d-flex align-center">
          <v-icon class="mr-2">mdi-robot-outline</v-icon>
          <span>Configuration Assistant</span>
          <v-spacer />
          <div v-if="configAgentReady" class="mr-1 d-flex align-center">
            <v-btn
              icon="mdi-video-plus-outline"
              variant="text"
              size="large"
              :disabled="workflowReviewSubmitting || !configAgentName"
              title="Video To Agent"
              @click="openVideoToAgentDialog"
            />
          </div>
          <v-btn
            v-if="configAgentReady && messages.length > 0"
            icon="mdi-delete-outline"
            variant="text"
            size="large"
            :loading="clearingChat"
            :disabled="clearingChat || isStreaming"
            title="Clear chat"
            @click="clearChat"
          />
          <v-btn icon="mdi-close" variant="text" size="small" @click="closeChat" />
        </v-card-title>
        <v-divider />

      <!-- Status check / provisioning handoff -->
      <template v-if="showConfigAgentPending">
        <v-card-text class="flex-grow-1 d-flex flex-column justify-center align-center">
          <v-progress-circular indeterminate color="primary" class="mb-4" />
          <h3 class="text-h6 mb-2">{{ configAgentPendingTitle }}</h3>
          <p class="text-body-2 text-center mb-0" style="max-width: 500px;">
            {{ configAgentPendingMessage }}
          </p>
        </v-card-text>
      </template>

      <!-- Status check failed -->
      <template v-else-if="configAgentStatusError">
        <v-card-text class="flex-grow-1 d-flex flex-column justify-center align-center">
          <v-icon size="64" color="warning" class="mb-4">mdi-alert-circle-outline</v-icon>
          <h3 class="text-h6 mb-2">Could not check Configuration Assistant</h3>
          <p class="text-body-2 text-center mb-6" style="max-width: 500px;">
            I could not confirm whether the assistant is ready yet. Please retry.
          </p>
          <v-btn color="primary" variant="outlined" :loading="configAgentStatusLoading" @click="onDialogOpen">
            Retry
          </v-btn>
        </v-card-text>
      </template>

      <!-- Model Selection Mode (existing user with providers but no config agent) -->
      <template v-else-if="!configAgentReady && availableProviders.length > 0">
        <v-card-text class="flex-grow-1 d-flex flex-column justify-center align-center">
          <v-icon size="64" color="primary" class="mb-4">mdi-robot-happy-outline</v-icon>
          <h3 class="text-h6 mb-2">Set Up Configuration Assistant</h3>
          <p class="text-body-2 text-center mb-6" style="max-width: 500px;">
            The configuration assistant helps you create agents, set up schedules, and manage tools.
            Select which AI model to use:
          </p>
          <v-radio-group v-model="selectedProviderKey" class="w-100 px-4" style="max-width: 400px;">
            <v-radio
              v-for="option in availableProviders"
              :key="option.providerKey"
              :label="`${option.modelName} (${option.providerKey})`"
              :value="option.providerKey"
            />
          </v-radio-group>
          <v-btn
            color="primary"
            variant="outlined"
            :loading="provisioning"
            :disabled="!selectedProviderKey || provisioning"
            @click="provisionWithSelectedProvider"
            class="mt-4"
          >
            Set Up Assistant
          </v-btn>
        </v-card-text>
      </template>

      <!-- Provider Setup Mode -->
      <template v-else-if="!configAgentReady">
        <v-card-text class="flex-grow-1 d-flex flex-column justify-center align-center">
          <h3 class="text-h6 mb-2">{{ hasExistingProviders ? 'Set Up Configuration Assistant' : 'Set Up an AI Provider' }}</h3>
          <p class="text-body-2 text-center mb-6" style="max-width: 500px;">
            {{ hasExistingProviders
              ? 'Select a provider to use for the configuration assistant. It helps you create agents, set up schedules, and manage tools.'
              : 'To get started, connect an AI provider. This will auto-provision the configuration assistant along with your first model and agent.'
            }}
          </p>
          <v-form class="w-100 px-4" style="max-width: 400px;" @submit.prevent="setupProvider" autocomplete="off">
            <input id="username" autocomplete="username" type="text" style="display:none">
            <input id="password" autocomplete="new-password" type="password" style="display:none">
            <v-select
              v-model="providerKey"
              :items="builtInProviders"
              label="Select Provider"
              variant="outlined"
              density="compact"
              class="mb-2"
            />
            <v-text-field
              v-if="!providerAlreadyConfigured"
              v-model="apiKey"
              label="API Key"
              type="password"
              variant="outlined"
              density="compact"
              class="mb-2"
              autocomplete="off"
            />
            <v-btn
              color="primary"
              block
              :loading="provisioning"
              :disabled="!providerKey || (!apiKey && !providerAlreadyConfigured) || provisioning"
              @click="setupProvider"
            >
              {{ providerAlreadyConfigured ? 'Set Up Assistant' : 'Connect Provider' }}
            </v-btn>
          </v-form>
        </v-card-text>
      </template>

      <!-- Chat Mode -->
      <template v-else>
        <v-card-text class="chat-area flex-grow-1 overflow-y-auto" ref="chatArea">
          <div :class="['chat-messages', { 'chat-fading-out': chatFadingOut }]">
            <v-row
              v-for="(message, index) in displayMessages"
              :key="index"
              :justify="message.role === 'user' ? 'end' : 'start'"
              no-gutters
              class="mb-4 chat-message-row"
              :style="chatFadingOut ? { transitionDelay: `${index * 40}ms` } : {}"
            >
              <v-col cols="12" sm="11">
                <div class="message-name text-caption mb-1" :class="message.role === 'user' ? 'text-right' : 'text-left'">
                  {{ message.role === 'user' ? 'You' : 'Config Assistant' }}
                </div>
                <div class="message-container" :class="message.role === 'user' ? 'user-message' : 'assistant-message'">
                  <!-- Tool Events -->
                  <ToolEventsPanel
                    v-if="message.role === 'assistant'"
                    :tool-events="message.toolEvents || []"
                    :show-activity="shouldShowToolActivity(message, index)"
                    compact
                  />
                  <FormattingCard
                    :content="message.content"
                    :expanded="true"
                    :title="null"
                    :streaming="streamingMessageIndex === index"
                    class="text-wrap text-break"
                  />
                </div>
              </v-col>
            </v-row>
          </div>
          <!-- Welcome entrance after clear -->
          <div v-if="welcomeEntering" class="welcome-entrance d-flex flex-column align-center justify-center" style="min-height: 200px;">
            <v-icon size="48" color="primary" class="welcome-icon mb-3">mdi-robot-happy-outline</v-icon>
            <span class="text-body-2 text-medium-emphasis welcome-text">Starting fresh...</span>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions class="flex-shrink-0 pa-3">
          <v-textarea
            v-model="userInput"
            placeholder="Ask about setting up agents, schedules, tools..."
            variant="outlined"
            density="compact"
            rows="2"
            auto-grow
            max-rows="6"
            hide-details
            class="flex-grow-1 mr-2"
            @keydown.enter.exact.prevent="sendMessage"
            :disabled="isStreaming"
          />
          <v-btn
            icon="mdi-send"
            color="primary"
            :disabled="!userInput.trim() || isStreaming || !configAgentName"
            :loading="isStreaming"
            @click="sendMessage"
          />
        </v-card-actions>
      </template>
      </v-card>
    </v-dialog>
    <VideoToAgentDialog
      :open="showVideoToAgentDialog"
      :processing="workflowVideoActive"
      :processing-label="workflowVideoStatusLabel"
      :error-message="workflowVideoError"
      @update:open="showVideoToAgentDialog = $event"
      @process="processWorkflowVideo"
    />
    <WorkflowVideoReviewDialog
      :open="workflowReviewOpen"
      :model-value="workflowReviewText"
      :submitting="workflowReviewSubmitting"
      :error-message="workflowReviewError"
      :can-submit-to-current-chat="canSubmitWorkflowReviewToCurrentChat"
      @update:open="workflowReviewOpen = $event"
      @update:modelValue="workflowReviewText = $event"
      @submit-to-current-chat="submitWorkflowReviewToCurrentChat"
      @submit-to-new-chat="submitWorkflowReviewToNewChat"
      @discard="discardWorkflowReview"
    />
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { nextTick } from 'vue'
import FormattingCard from '@/components/cards/FormattingCard.vue'
import ToolEventsPanel from '@/components/sub/ToolEventsPanel.vue'
import VideoToAgentDialog from '@/components/dialogs/VideoToAgentDialog.vue'
import WorkflowVideoReviewDialog from '@/components/dialogs/WorkflowVideoReviewDialog.vue'
import {
  startStreamingAgentCompletionRequest,
  getConfigAgentStatus,
  provisionConfigAgent,
  getConfigChatHistory,
  newConfigChatSession
} from '@/utils/api'
import type { ConfigAgentProviderOption } from '@/utils/api'
import { GenerateResultProviderState, ModelMessage, ToolEvent } from '@/types/msq'
import type { VideoProcessingOptionsInput, VideoProcessingRunRecord } from '@/types/videoProcessing'
import { connectSSE, addEventListener, removeEventListener, updateSessionId, getState } from '@/utils/sse'
import { PtcTranscriptFilter } from '@/utils/ptcSuppression'
import { getRefreshTargetsForToolName } from '@/utils/configRefresh'

@Options({
  name: 'ConfigurationChatDialog',
  components: { FormattingCard, ToolEventsPanel, VideoToAgentDialog, WorkflowVideoReviewDialog },
  watch: {
    showConfigChat(val: boolean) {
      if (val) {
        (this as ConfigurationChatDialog).onDialogOpen()
      }
    },
    videoProcessingReviewDraft(nextDraft: { runId: string; markdown: string; updatedAt: number } | null) {
      ;(this as ConfigurationChatDialog).handleWorkflowReviewDraftChanged(nextDraft)
    }
  }
})
export default class ConfigurationChatDialog extends Vue {
  messages: ModelMessage[] = []
  userInput = ''
  isStreaming = false
  configAgentReady = false
  configAgentStatusChecked = false
  configAgentStatusLoading = false
  configAgentStatusError = false
  configAgentName: string | null = null
  provisioning = false
  providerKey: string | null = null
  apiKey = ''
  availableProviders: ConfigAgentProviderOption[] = []
  selectedProviderKey: string | null = null
  clearingChat = false
  chatFadingOut = false
  welcomeEntering = false
  currentSessionId: string | null = null
  workflowReviewText = ''
  workflowReviewOpen = false
  workflowReviewError: string | null = null

  private streamBuffer = ''
  private streamFlushTimer: ReturnType<typeof setTimeout> | null = null
  private streamingMessageIndex: number | null = null
  private readonly streamFlushIntervalMs = 40
  private ptcFilter = new PtcTranscriptFilter()
  private pendingContextMessages: any[] | null = null
  private conversationProviderState: GenerateResultProviderState | undefined = undefined

  // Tool activity state
  private toolActivityVisible = false
  private toolActivityMessageIndex: number | null = null
  private activeToolCallCount = 0
  private activeToolEventMessageIndex: number | null = null
  private eventListener: ((event: any) => void) | null = null
  private sseSessionId: string | null = null
  private seenEventKeys = new Set<string>()

  get displayMessages() {
    return this.messages.filter(m =>
      m.role !== 'tool' && !(m.role === 'assistant' && m.tool_calls && m.tool_calls.length > 0 && !m.content)
    )
  }

  private applyPendingContextMessagesToStreamingMessage(): void {
    if (this.streamingMessageIndex === null || !this.pendingContextMessages || this.pendingContextMessages.length === 0) {
      return
    }

    const streamingMessageIndex = this.streamingMessageIndex
    const currentStreamingMessage = this.messages[streamingMessageIndex]
    if (!currentStreamingMessage) {
      this.pendingContextMessages = null
      return
    }

    const normalizedContextMessages: ModelMessage[] = this.pendingContextMessages.map((message: ModelMessage) => ({
      ...message,
      content: message.content ?? ''
    }))

    const authoritativeFinalAssistant = normalizedContextMessages[normalizedContextMessages.length - 1]
    const priorContextMessages = normalizedContextMessages.slice(0, -1)

    const mergedFinalAssistant: ModelMessage =
      authoritativeFinalAssistant && authoritativeFinalAssistant.role === 'assistant'
        ? {
            ...currentStreamingMessage,
            ...authoritativeFinalAssistant,
            content: authoritativeFinalAssistant.content ?? currentStreamingMessage.content ?? '',
            toolEvents: currentStreamingMessage.toolEvents ?? authoritativeFinalAssistant.toolEvents,
            tokenUsage: currentStreamingMessage.tokenUsage ?? authoritativeFinalAssistant.tokenUsage
          }
        : currentStreamingMessage

    this.messages.splice(streamingMessageIndex, 1, mergedFinalAssistant)

    if (priorContextMessages.length > 0) {
      this.messages.splice(streamingMessageIndex, 0, ...priorContextMessages)
    }

    this.pendingContextMessages = null
  }

  private beginToolEventRouting(messageIndex: number): void {
    this.activeToolEventMessageIndex = messageIndex
  }

  private clearToolEventRouting(): void {
    this.activeToolEventMessageIndex = null
  }

  get showConfigChat(): boolean {
    return this.$store.getters.showConfigChat
  }
  set showConfigChat(val: boolean) {
    this.$store.dispatch('toggleConfigChat', val)
  }

  get builtInProviders(): string[] {
    return this.$store.getters.builtInProviders
  }

  get providerConfigs(): Record<string, unknown> {
    return this.$store.getters.providerConfigs ?? {}
  }

  get hasExistingProviders(): boolean {
    return Object.keys(this.providerConfigs).length > 0
  }

  get providerAlreadyConfigured(): boolean {
    return !!this.providerKey && this.providerKey in this.providerConfigs
  }

  get workflowVideoActiveRun(): VideoProcessingRunRecord | null {
    return this.$store.getters.videoProcessingActiveRun ?? null
  }

  get workflowVideoUploading(): boolean {
    return this.$store.getters.videoProcessingUploading ?? false
  }

  get workflowVideoError(): string | null {
    return this.$store.getters.videoProcessingError ?? null
  }

  get videoProcessingReviewDraft(): { runId: string; markdown: string; updatedAt: number } | null {
    return this.$store.getters.videoProcessingReviewDraft ?? null
  }

  get workflowVideoActive(): boolean {
    return this.$store.getters.videoProcessingHasActiveRun ?? false
  }

  get workflowVideoHasPendingReview(): boolean {
    return this.$store.getters.videoProcessingHasPendingReview ?? false
  }

  get workflowReviewSubmitting(): boolean {
    return this.$store.getters.configAssistantWorkflowSubmitInFlight ?? false
  }

  get showVideoToAgentDialog(): boolean {
    return this.$store.getters.showVideoToAgentDialog ?? false
  }
  set showVideoToAgentDialog(value: boolean) {
    this.$store.dispatch('toggleVideoToAgentDialog', value)
  }

  get workflowVideoStatusLabel(): string {
    if (this.workflowVideoUploading) {
      return 'Uploading...'
    }
    const run = this.workflowVideoActiveRun
    if (run?.status === 'queued') {
      return 'Queued...'
    }
    if (run?.status === 'running') {
      return 'Analyzing...'
    }
    return 'Processing...'
  }

  get canSubmitWorkflowReviewToCurrentChat(): boolean {
    return !!this.currentSessionId && !this.workflowReviewSubmitting && !this.isStreaming
  }

  get showConfigAgentPending(): boolean {
    return this.configAgentStatusLoading || !this.configAgentStatusChecked || (this.provisioning && !this.configAgentReady)
  }

  get configAgentPendingTitle(): string {
    return this.provisioning ? 'Setting up Configuration Assistant' : 'Checking Configuration Assistant'
  }

  get configAgentPendingMessage(): string {
    return this.provisioning
      ? 'This can take a moment after connecting your provider.'
      : 'Checking whether your assistant is ready.'
  }

  onDialogToggle(val: boolean) {
    if (!val) this.closeChat()
  }

  async onDialogOpen() {
    this.configAgentStatusChecked = false
    this.configAgentStatusLoading = true
    this.configAgentStatusError = false
    try {
      await this.checkConfigAgentStatus()
    } finally {
      this.configAgentStatusLoading = false
    }

    if (this.configAgentReady) {
      this.setupSSEListener()
      const response = await getConfigChatHistory()
      this.currentSessionId = response?.id ?? null
      if (this.currentSessionId) {
        this.syncSseSession(this.currentSessionId)
      }
      if (response && response.messages.length > 0) {
        this.messages = this.ptcFilter.sanitizeMessages(response.messages)
        this.conversationProviderState = response.providerState
      } else {
        this.messages = []
        this.conversationProviderState = undefined
        this.addWelcomeMessage()
      }
      await this.$store.dispatch('subscribeToActiveRun')
      this.applyConfigChatSeed()
      if (this.videoProcessingReviewDraft) {
        this.workflowReviewText = this.buildWorkflowReviewDocument(this.videoProcessingReviewDraft.markdown)
        this.workflowReviewError = null
        this.workflowReviewOpen = true
      }
    }
  }

  async checkConfigAgentStatus() {
    const status = await getConfigAgentStatus()
    this.configAgentStatusChecked = true
    this.configAgentStatusError = status == null
    this.configAgentReady = status?.provisioned ?? false
    this.configAgentName = status?.agentName ?? null
    this.availableProviders = status?.availableProviders ?? []
    // Auto-select if only one option
    if (this.availableProviders.length === 1) {
      this.selectedProviderKey = this.availableProviders[0].providerKey
    }
  }

  async setupProvider() {
    if (!this.providerKey) return
    if (!this.providerAlreadyConfigured && !this.apiKey) return
    this.provisioning = true
    try {
      if (!this.providerAlreadyConfigured) {
        await this.$store.dispatch('addProvider', {
          providerKey: this.providerKey,
          apiKey: this.apiKey,
        })
        // First provider of a brand-new account: yield the screen to the user-mode choice
        // (UserModeDialog shows the moment the provider lands in the store, and reopens this dialog
        // once a mode is picked). Provisioning continues below either way.
        if (this.$store.getters.awaitingModeChoice) {
          this.showConfigChat = false
        }
      } else {
        // Provider exists; trigger config agent provisioning directly
        const result = await provisionConfigAgent(this.providerKey)
        if (!result || !result.success) {
          this.$store.dispatch('updateSnackbar', {
            text: result?.error || 'Failed to provision config agent. Please try again.',
            timeout: 5000,
          })
          this.provisioning = false
          return
        }
      }
      let attempts = 0
      while (attempts < 20) {
        await new Promise(r => setTimeout(r, 1500))
        await this.checkConfigAgentStatus()
        if (this.configAgentReady) break
        attempts++
      }
      if (this.configAgentReady) {
        this.$store.dispatch('markConfigResourcesDirty', ['agents'])
        this.addWelcomeMessage()
        this.applyConfigChatSeed()
      }
    } catch {
      this.$store.dispatch('updateSnackbar', {
        text: 'Failed to set up provider. Please try again.',
        timeout: 5000,
      })
    } finally {
      this.provisioning = false
    }
  }

  async provisionWithSelectedProvider() {
    if (!this.selectedProviderKey) return
    this.provisioning = true
    try {
      const result = await provisionConfigAgent(this.selectedProviderKey)
      if (!result || !result.success) {
        this.$store.dispatch('updateSnackbar', {
          text: result?.error || 'Failed to provision config agent. Please try again.',
          timeout: 5000,
        })
        this.provisioning = false
        return
      }
      // Poll for readiness
      let attempts = 0
      while (attempts < 20) {
        await new Promise(r => setTimeout(r, 1500))
        await this.checkConfigAgentStatus()
        if (this.configAgentReady) break
        attempts++
      }
      if (this.configAgentReady) {
        this.$store.dispatch('markConfigResourcesDirty', ['agents'])
        this.addWelcomeMessage()
        this.applyConfigChatSeed()
      }
    } catch {
      this.$store.dispatch('updateSnackbar', {
        text: 'Failed to set up configuration assistant. Please try again.',
        timeout: 5000,
      })
    } finally {
      this.provisioning = false
    }
  }

  get configChatSeed(): string | null {
    return this.$store.getters.configChatSeed ?? null
  }

  // When opened to create a specific kind of automation, send a starter request on the user's
  // behalf so the config agent starts building immediately. It's a normal user turn (no synthetic
  // assistant message), so the model gets a clean opening to respond to. If we can't send yet
  // (agent not ready / mid-stream), fall back to pre-filling the composer for a manual send.
  applyConfigChatSeed(): void {
    const seed = this.configChatSeed
    if (!seed) return
    this.$store.dispatch('clearConfigChatSeed')
    if (this.isStreaming || !this.configAgentName) {
      if (!this.userInput.trim()) {
        this.userInput = seed
      }
      return
    }
    this.setupSSEListener()
    this.userInput = seed
    void this.sendMessage()
  }

  addWelcomeMessage() {
    if (this.messages.length > 0) return
    this.messages.push({
      role: 'assistant',
      content: "I'm your MissionSquad Configuration Assistant. I can help you set up AI agents, configure scheduled tasks with email delivery, manage tools, and more. What would you like to set up?"
    })
  }

  private buildWorkflowReviewDocument(markdown: string): string {
    const normalized = this.stripMarkdownCodeFences(markdown).trim()
    const instructionPrefix = [
      'Here is a workflow description extracted from a video.',
      'Create the agent or agents needed to perform the actions described below.',
      'If multiple agents are required, plan and create all necessary agents.',
      'If more than one agent is needed, create a workflow or factory to coordinate the steps into a cohesive flow.',
      '',
      ''
    ].join('\n')

    return `${instructionPrefix}${normalized}`
  }

  private stripMarkdownCodeFences(markdown: string): string {
    const trimmed = markdown.trim()
    const fenced = trimmed.match(/^```[^\n]*\n([\s\S]*?)\n```$/)
    if (fenced && typeof fenced[1] === 'string') {
      return fenced[1].trim()
    }
    return trimmed
  }

  handleWorkflowReviewDraftChanged(nextDraft: { runId: string; markdown: string; updatedAt: number } | null): void {
    if (nextDraft) {
      this.showVideoToAgentDialog = false
      this.workflowReviewText = this.buildWorkflowReviewDocument(nextDraft.markdown)
      this.workflowReviewError = null
      this.workflowReviewOpen = true
      return
    }

    if (!this.workflowReviewSubmitting) {
      this.workflowReviewOpen = false
    }
  }

  openVideoToAgentDialog(): void {
    this.showVideoToAgentDialog = true
  }

  async processWorkflowVideo(payload: { file: File; clipStartSec: number; clipEndSec: number }): Promise<void> {
    const maxBytes = 512 * 1024 * 1024
    if (payload.file.size > maxBytes) {
      await this.$store.dispatch('updateSnackbar', {
        text: 'Video exceeds the 512 MiB upload limit.',
        timeout: 5000
      })
      return
    }

    const options: VideoProcessingOptionsInput = {
      clipStartSec: payload.clipStartSec,
      clipEndSec: payload.clipEndSec
    }

    this.workflowReviewError = null
    await this.$store.dispatch('startWorkflowVideoRun', { file: payload.file, options })
  }

  async cancelWorkflowVideoProcessing(): Promise<void> {
    await this.$store.dispatch('cancelActiveRun')
  }

  async discardWorkflowReview(): Promise<void> {
    this.workflowReviewError = null
    this.workflowReviewOpen = false
    this.workflowReviewText = ''
    await this.$store.dispatch('dismissWorkflowReviewDraft')
  }

  private appendStreamingMessages(userContent: string): number {
    this.messages.push({ role: 'user', content: userContent })
    this.messages.push({ role: 'assistant', content: '' })
    return this.messages.length - 1
  }

  private beginStreamingRequest(assistantMessageIndex: number): void {
    this.streamingMessageIndex = assistantMessageIndex
    this.beginToolEventRouting(assistantMessageIndex)
    this.isStreaming = true
    this.seenEventKeys.clear()
    this.ptcFilter.reset()
    this.pendingContextMessages = null
  }

  private finalizeStreamingRequest(): void {
    this.flushStreamBuffer()
    this.hideToolActivity()
    this.applyPendingContextMessagesToStreamingMessage()
    this.isStreaming = false
    this.streamingMessageIndex = null
    this.scrollToBottom()
  }

  private handleStreamingChunk(chunk: string): void {
    const filtered = this.ptcFilter.filterChunk(chunk)
    if (!filtered) {
      return
    }
    this.handleResponseStreamingStarted()
    this.streamBuffer += filtered
    if (!this.streamFlushTimer) {
      this.streamFlushTimer = setTimeout(() => this.flushStreamBuffer(), this.streamFlushIntervalMs)
    }
  }

  private handleStreamingMeta(meta: { runId?: string; contextMessages?: any[]; providerState?: GenerateResultProviderState }): void {
    if (meta.contextMessages && meta.contextMessages.length > 0) {
      this.pendingContextMessages = meta.contextMessages
    }
    if (meta.providerState) {
      this.conversationProviderState = meta.providerState
    }
  }

  private handleStreamingError(error: Error): void {
    this.flushStreamBuffer()
    this.hideToolActivity()
    if (this.streamingMessageIndex !== null) {
      this.messages[this.streamingMessageIndex].content = `Error: ${error.message}. Please try again.`
    }
    this.isStreaming = false
    this.streamingMessageIndex = null
  }

  private clearPendingStreamUi(): void {
    this.flushStreamBuffer()
    this.hideToolActivity()
    this.isStreaming = false
    this.streamingMessageIndex = null
    this.pendingContextMessages = null
  }

  async sendMessage() {
    const text = this.userInput.trim()
    if (!text || this.isStreaming || !this.configAgentName) return
    this.userInput = ''
    const assistantMessageIndex = this.appendStreamingMessages(text)
    this.beginStreamingRequest(assistantMessageIndex)

    try {
      const sessionId = await this.ensureCurrentSessionId()
      this.syncSseSession(sessionId)

      const streamHandle = startStreamingAgentCompletionRequest({
        messages: this.messages.slice(0, -1),
        name: this.configAgentName,
        sessionId,
        providerState: this.conversationProviderState,
        onChunk: (chunk: string) => this.handleStreamingChunk(chunk),
        onComplete: () => this.finalizeStreamingRequest(),
        onMeta: (meta: { runId?: string; contextMessages?: any[]; providerState?: GenerateResultProviderState }) =>
          this.handleStreamingMeta(meta),
        onError: (error: Error) => this.handleStreamingError(error),
      })
      await streamHandle.promise
    } catch (error) {
      this.hideToolActivity()
      if (this.streamingMessageIndex !== null) {
        const message = error instanceof Error ? error.message : 'Failed to start chat stream.'
        this.messages[this.streamingMessageIndex].content = `Error: ${message}. Please try again.`
      }
      this.isStreaming = false
      this.streamingMessageIndex = null
    }
  }

  async submitWorkflowReviewToCurrentChat(): Promise<void> {
    const text = this.workflowReviewText.trim()
    if (!text || !this.configAgentName || !this.currentSessionId || this.isStreaming || this.workflowReviewSubmitting) {
      return
    }

    this.workflowReviewError = null
    this.workflowReviewOpen = false
    this.showConfigChat = true
    const assistantMessageIndex = this.appendStreamingMessages(text)
    this.beginStreamingRequest(assistantMessageIndex)
    this.syncSseSession(this.currentSessionId)

    try {
      await this.$store.dispatch('submitWorkflowReview', {
        mode: 'current-chat',
        agentName: this.configAgentName,
        messages: this.messages.slice(0, -1),
        currentSessionId: this.currentSessionId,
        providerState: this.conversationProviderState,
        onSessionResolved: (sessionId: string) => {
          this.currentSessionId = sessionId
          this.syncSseSession(sessionId)
        },
        onChunk: (chunk: string) => this.handleStreamingChunk(chunk),
        onComplete: () => this.finalizeStreamingRequest(),
        onMeta: (meta: { runId?: string; contextMessages?: any[]; providerState?: GenerateResultProviderState }) =>
          this.handleStreamingMeta(meta),
        onError: (error: Error) => this.handleStreamingError(error)
      })

      this.workflowReviewOpen = false
      this.workflowReviewText = ''
      this.workflowReviewError = null
      await this.$store.dispatch('dismissWorkflowReviewDraft')
    } catch (error) {
      this.workflowReviewOpen = true
      this.workflowReviewError = error instanceof Error ? error.message : 'Failed to send workflow to current chat.'
    }
  }

  async submitWorkflowReviewToNewChat(): Promise<void> {
    const text = this.workflowReviewText.trim()
    if (!text || !this.configAgentName || this.isStreaming || this.workflowReviewSubmitting) {
      return
    }

    const previousMessages = structuredClone(this.messages)
    const previousConversationProviderState = this.conversationProviderState == null
      ? undefined
      : structuredClone(this.conversationProviderState)
    const previousSessionId = this.currentSessionId

    this.workflowReviewError = null
    this.workflowReviewOpen = false
    this.showConfigChat = true

    try {
      await this.$store.dispatch('submitWorkflowReview', {
        mode: 'new-chat',
        agentName: this.configAgentName,
        messages: [{ role: 'user', content: text }],
        currentSessionId: null,
        providerState: undefined,
        onSessionResolved: (sessionId: string) => {
          try {
            this.clearPendingStreamUi()
            this.messages = []
            this.conversationProviderState = undefined
            this.currentSessionId = sessionId
            this.syncSseSession(sessionId)
            const assistantMessageIndex = this.appendStreamingMessages(text)
            this.beginStreamingRequest(assistantMessageIndex)
          } catch (error) {
            this.clearPendingStreamUi()
            this.messages = previousMessages
            this.conversationProviderState = previousConversationProviderState
            this.currentSessionId = previousSessionId
            if (previousSessionId) {
              this.syncSseSession(previousSessionId)
            }
            throw error instanceof Error ? error : new Error('Failed to prepare the new configuration chat session.')
          }
        },
        onChunk: (chunk: string) => this.handleStreamingChunk(chunk),
        onComplete: () => this.finalizeStreamingRequest(),
        onMeta: (meta: { runId?: string; contextMessages?: any[]; providerState?: GenerateResultProviderState }) =>
          this.handleStreamingMeta(meta),
        onError: (error: Error) => this.handleStreamingError(error)
      })

      this.workflowReviewOpen = false
      this.workflowReviewText = ''
      this.workflowReviewError = null
      await this.$store.dispatch('dismissWorkflowReviewDraft')
    } catch (error) {
      this.clearPendingStreamUi()
      this.messages = previousMessages
      this.conversationProviderState = previousConversationProviderState
      this.currentSessionId = previousSessionId
      if (previousSessionId) {
        this.syncSseSession(previousSessionId)
      }
      this.workflowReviewOpen = true
      this.workflowReviewError = error instanceof Error ? error.message : 'Failed to start a new workflow chat.'
    }
  }

  private flushStreamBuffer() {
    if (this.streamBuffer && this.streamingMessageIndex !== null) {
      this.messages[this.streamingMessageIndex].content += this.streamBuffer
      this.streamBuffer = ''
      this.scrollToBottom()
    }
    if (this.streamFlushTimer) {
      clearTimeout(this.streamFlushTimer)
      this.streamFlushTimer = null
    }
  }

  private scrollToBottom() {
    nextTick(() => {
      const chatArea = this.$refs.chatArea as HTMLElement | undefined
      if (chatArea) chatArea.scrollTop = chatArea.scrollHeight
    })
  }

  private async ensureCurrentSessionId(): Promise<string> {
    if (this.currentSessionId) {
      return this.currentSessionId
    }

    const result = await newConfigChatSession()
    if (!result?.id) {
      throw new Error('Failed to create a config chat session.')
    }

    this.currentSessionId = result.id
    return result.id
  }

  private syncSseSession(sessionId: string): void {
    updateSessionId(sessionId)
    const state = getState()
    if (!state.isConnected || state.sessionId !== sessionId) {
      connectSSE(sessionId)
    }
    this.sseSessionId = sessionId
  }

  async clearChat(): Promise<void> {
    this.clearingChat = true
    try {
      // Phase 1: fade out messages (staggered per row)
      this.chatFadingOut = true
      const fadeOutMs = Math.min(this.messages.length * 40 + 300, 600)
      const sessionPromise = newConfigChatSession()
      await new Promise(r => setTimeout(r, fadeOutMs))

      // Phase 2: swap content — show brief welcome entrance
      this.messages = []
      this.conversationProviderState = undefined
      this.welcomeEntering = true
      this.chatFadingOut = false

      const result = await sessionPromise
      this.currentSessionId = result ? result.id : null
      if (this.currentSessionId) {
        this.syncSseSession(this.currentSessionId)
      } else {
        this.sseSessionId = null
      }

      // Let the entrance animation play
      await new Promise(r => setTimeout(r, 600))
      this.welcomeEntering = false
      this.addWelcomeMessage()
    } catch {
      this.chatFadingOut = false
      this.welcomeEntering = false
      this.$store.dispatch('updateSnackbar', {
        text: 'Failed to clear chat history. Please try again.',
        timeout: 5000
      })
    } finally {
      this.clearingChat = false
    }
  }

  closeChat() {
    this.cleanupSSEListener()
    this.workflowReviewOpen = false
    this.showConfigChat = false
  }

  // --- SSE & Tool Event Methods ---

  private setupSSEListener(): void {
    if (this.eventListener) {
      return
    }

    // Clear dedup set for each new SSE listener setup
    this.seenEventKeys.clear()

    this.eventListener = (event: { type: string; message?: string; data?: unknown; sessionId?: string; clientId?: string }) => {
      if (!this.isUiToolEventFrame(event)) return
      if (event.sessionId !== this.sseSessionId) return
      if (this.activeToolEventMessageIndex === null) return

      // Deduplicate: backend may emit the same event multiple times due to accumulated listeners
      const d = event.data as Record<string, unknown> | undefined
      const dedupKey = `${event.type}:${d?.toolName ?? ''}:${d?.requestId ?? d?.time ?? ''}`
      if (this.seenEventKeys.has(dedupKey)) return
      this.seenEventKeys.add(dedupKey)

      const toolEvent: ToolEvent = {
        type: event.type,
        message: event.message,
        data: event.data,
        sessionId: event.sessionId,
        clientId: event.clientId,
        timestamp: Date.now()
      }

      const currentMessage = this.messages[this.activeToolEventMessageIndex]
      if (!currentMessage) return
      if (!currentMessage.toolEvents) {
        currentMessage.toolEvents = []
      }
      currentMessage.toolEvents.push(toolEvent)
      if (event.type === 'tool_end') {
        const toolName = typeof d?.toolName === 'string' ? d.toolName : null
        const dirtyTargets = getRefreshTargetsForToolName(toolName)
        if (dirtyTargets.length > 0) {
          this.$store.dispatch('markConfigResourcesDirty', dirtyTargets)
        }
      }
      this.handleToolActivityEvent(event.type)
      this.scrollToBottom()
    }

    addEventListener(this.eventListener)
  }

  private cleanupSSEListener(): void {
    if (this.eventListener) {
      removeEventListener(this.eventListener)
      this.eventListener = null
    }
    this.sseSessionId = null
    this.hideToolActivity()
    this.clearToolEventRouting()
  }

  private isUiToolEventFrame(v: unknown): v is { type: string; message?: string; data?: unknown; sessionId?: string; clientId?: string } {
    return typeof v === 'object' && v != null && 'type' in (v as Record<string, unknown>)
  }

  private showToolActivity(messageIndex: number): void {
    this.toolActivityMessageIndex = messageIndex
    this.toolActivityVisible = true
  }

  private hideToolActivity(): void {
    this.toolActivityVisible = false
    this.toolActivityMessageIndex = null
    this.activeToolCallCount = 0
  }

  private handleToolActivityEvent(eventType: string): void {
    if (this.activeToolEventMessageIndex === null) return

    if (eventType === 'tool_start') {
      this.activeToolCallCount += 1
      this.showToolActivity(this.activeToolEventMessageIndex)
      return
    }

    if (eventType === 'tool_end' || eventType === 'tool_error') {
      this.activeToolCallCount = Math.max(0, this.activeToolCallCount - 1)
    }
  }

  private handleResponseStreamingStarted(): void {
    if (!this.toolActivityVisible) return
    this.hideToolActivity()
  }

  shouldShowToolActivity(message: ModelMessage, index: number): boolean {
    return message.role === 'assistant'
      && this.toolActivityVisible
      && this.toolActivityMessageIndex === index
  }

}
</script>

<style lang="scss" scoped>
.config-chat-card {
  overflow: hidden;
}

.chat-area {
  overflow-y: auto;
}

.message-container {
  border-radius: 8px;
  padding: 4px 8px;

  &.user-message {
    background-color: rgba(var(--v-theme-primary), 0.08);
  }

  &.assistant-message {
    background-color: rgba(var(--v-theme-surface-variant), 0.3);
  }
}

// Clear-chat animation: staggered fade-out per message row
.chat-messages {
  transition: opacity 0.2s ease;
}

.chat-fading-out .chat-message-row {
  opacity: 0;
  transform: translateY(-8px) scale(0.97);
  transition: opacity 0.3s ease, transform 0.3s ease;
}

// Welcome entrance after clear
.welcome-entrance {
  animation: welcomeEntrance 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

.welcome-icon {
  animation: iconPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s both;
}

.welcome-text {
  animation: textFadeUp 0.4s ease 0.25s both;
}

@keyframes welcomeEntrance {
  0% { opacity: 0; }
  100% { opacity: 1; }
}

@keyframes iconPop {
  0% {
    opacity: 0;
    transform: scale(0.3) rotate(-15deg);
  }
  100% {
    opacity: 1;
    transform: scale(1) rotate(0deg);
  }
}

@keyframes textFadeUp {
  0% {
    opacity: 0;
    transform: translateY(12px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

</style>
